/* eslint-disable i18next/no-literal-string */
/* eslint-disable camelcase */
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import widget_icons_light from '@hotjar/common/assets/widget_icons_light.png';
import type { Invitation } from '../Invitation';

/* HEADS UP!
 *
 * This is using literal styles instead of DS components on purpose
 * We are matching the invitation design from the client script
 * Ideally this would be a preview just like we have for other widgets
 *
 */

const Wrapper = styled.div`
  padding: 0 ${tokens.spacer.x7};
  background: ${tokens.color.neutral_100};
  flex: 1;
  align-items: center;
  justify-content: center;
  display: flex;
  margin-right: -72px;
  margin-left: 72px;
`;

const InvitationContainer = styled.div`
  position: relative;

  width: 80%;
  max-width: 550px;
  min-width: 434px;

  background: white;
  border-radius: 6px;
  box-shadow: 0 5px 13px 0 rgba(0, 0, 0, 0.65) !important;
`;

const CloseButton = styled.button`
  background-image: url(${widget_icons_light}) !important;
  background-repeat: no-repeat;
  background-position: -120px 0;
  background-color: transparent !important;
  cursor: pointer;
  opacity: 0.6;
  position: absolute;
  right: 10px;
  top: 10px;
  width: 16px;
  height: 16px;
  border: 0 !important;

  :hover {
    opacity: 1;
  }
`;

const Content = styled.div`
  padding: 50px 20px;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h3`
  white-space: pre-wrap;
  overflow-wrap: break-word;
  padding: 0 20px 20px 20px;
  font-size: 24px;
  color: #333333;
  font-weight: 400;
  max-width: 100%;
  font-family: Arial, sans-serif, Tahoma !important;
`;

const Description = styled.p`
  white-space: pre-wrap;
  overflow-wrap: break-word;

  padding: 0 30px 40px 30px;
  font-weight: normal;
  font-size: 16px;
  line-height: 25px;
  color: #666666 !important;
  max-width: 100%;
  margin: 0;
  font-family: Arial, sans-serif, Tahoma !important;
`;

const GoToSurvey = styled.button`
  font-size: 18px !important;
  padding: 16px 26px !important;
  text-decoration: none !important;

  background: #324fbe;
  color: #ffffff;

  font-weight: 500 !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;

  border-radius: 4px;
  max-width: 100%;
  font-family: Arial, sans-serif, Tahoma !important;
`;

const Close = styled.button`
  display: block;
  margin: 20px auto 0;
  color: #666666;
  padding: 0 !important;
  cursor: pointer;
  text-decoration: underline;
  font-size: 13px !important;
  border: 0 !important;

  :hover {
    color: #333333;
  }

  background-color: transparent;
  font-family: Arial, sans-serif, Tahoma !important;
`;

const BrandingIcon = styled.span`
  background-image: url(${widget_icons_light});
  background-repeat: no-repeat;
  background-position: -16px -1px;
  margin-right: 4px;
  width: 16px;
  height: 16px;
  display: inline-block !important;
  zoom: 1;
  vertical-align: middle;
`;

const BrandingLink = styled.span`
  color: #666666;
  position: absolute;
  left: 0;
  bottom: 0;
  margin-bottom: 10px;
  margin-left: 10px;
  font-size: 11px;
  height: 16px;
  font-family: Arial, sans-serif, Tahoma !important;
`;

const HotjarLink = styled.span`
  color: #666666;
  text-decoration: underline !important; // fix user styles
  :hover {
    cursor: pointer;
  }
  font-family: Arial, sans-serif, Tahoma !important;
`;

export const InvitationPreview = ({
  invitation,
  showBranding,
}: {
  invitation: Invitation;
  showBranding: boolean;
}): JSX.Element => {
  const { title, description, button, close } = invitation;
  return (
    <Wrapper>
      <InvitationContainer>
        <CloseButton type="button" aria-label="Close" />
        <Content>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <GoToSurvey>{button}</GoToSurvey>
          <Close>{close}</Close>
          {showBranding && (
            <BrandingLink>
              <BrandingIcon />
              Not using <HotjarLink>Hotjar</HotjarLink> yet?
            </BrandingLink>
          )}
        </Content>
      </InvitationContainer>
    </Wrapper>
  );
};
